import { samlQueryKeys } from '@/config/queryKeys';
import { adminRoles } from '@/constants';
import { useRouteProtection } from '@/hooks';
import { apiClient } from '@/lib/api-client';
import { scrollbars } from '@/styles/index.module.css';
import type { SAMLMetadata, SAMLProviderFormDefaults } from '@/types';
import { isForbiddenError } from '@/types/errors';
import { Group, Popover, Tooltip, getStackClassName, noop } from '@nautilusbio/nb-design-system';
import Button from '@nautilusbio/nb-design-system/Button';
import Input from '@nautilusbio/nb-design-system/Input';
import type { InputProps } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import Tabs, { Tab, TabList, TabPanel } from '@nautilusbio/nb-design-system/Tabs';
import Text from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import Upload from '@nautilusbio/nb-design-system/Upload';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { useLoaderData, useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { Cog, Lock } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { CopyText, FormSectionTitle } from './components';
import styles from './styles.module.css';

// Type for API errors with potential authorization properties
interface ApiError extends Error {
  isUnauthorized?: boolean;
  status?: number;
}

const sharedInputProps: Partial<InputProps> = {
  fullWidth: true,
  isCopyable: true,
};

const fetchSAMLMetadata = async () => {
  const data = await apiClient.get<SAMLMetadata>(samlQueryKeys.endpoints.metadata());
  return {
    recipient: data?.recipient ?? '',
    audience: data?.audience ?? '',
  };
};

const fetchSAMLProvider = async () => {
  const data = await apiClient.get<SAMLProviderFormDefaults>(samlQueryKeys.endpoints.provider());
  return {
    provider_name: data?.provider_name ?? '',
    email_domain: data?.email_domain ?? '',
    metadata_url: data?.metadata_url ?? '',
    metadata_file: data?.metadata_file ?? '',
    attr_email: data?.attr_email ?? '',
    attr_first_name: data?.attr_first_name ?? '',
    attr_last_name: data?.attr_last_name ?? '',
    attr_roles: data?.attr_roles ?? '',
  };
};

const AuthPage: FC = () => {
  // Protect this route - will redirect if user loses SuperAdmin role or switches org
  useRouteProtection(adminRoles);

  const [showOptions, setShowOptions] = useState<boolean>(false);

  const onCloseHandler = () => setShowOptions(false);
  const { t } = useTranslation('auth');
  const fileRef = useRef<File | null>(null);
  const { samlMetadataDefault, providerDefaults: providerDefaultsInitial } = useLoaderData({ from: '/auth/' }) as {
    samlMetadataDefault: SAMLMetadata;
    providerDefaults: SAMLProviderFormDefaults;
  };

  const navigate = useNavigate();

  // SAML Metadata Query with manual refetch and select
  const {
    data: samlMetadata,
    isLoading: loadingMetadata,
    error: errorMetadata,
    refetch: refetchMetadata,
    isFetching: isFetchingMetadata,
  } = useQuery<SAMLMetadata>({
    queryKey: samlQueryKeys.metadata(),
    queryFn: fetchSAMLMetadata,
    initialData: samlMetadataDefault,
    staleTime: 24 * 60 * 60 * 1000, // 1 day
    retry: (failureCount, error) => {
      const apiError = error as ApiError;
      if (apiError?.isUnauthorized || error?.name === 'UnauthorizedError' || apiError?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
    select: data => ({
      recipient: data.recipient,
      audience: data.audience,
    }),
  });

  const {
    data: provider,
    isLoading: loadingProvider,
    error: errorProvider,
    refetch: refetchProvider,
    isFetching: isFetchingProvider,
  } = useQuery<SAMLProviderFormDefaults>({
    queryKey: samlQueryKeys.provider(),
    queryFn: fetchSAMLProvider,
    initialData: providerDefaultsInitial,
    staleTime: 24 * 60 * 60 * 1000, // 1 day
    retry: (failureCount, error) => {
      const apiError = error as ApiError;
      if (apiError?.isUnauthorized || error?.name === 'UnauthorizedError' || apiError?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
    select: data => ({
      provider_name: data.provider_name ?? '',
      email_domain: data.email_domain ?? '',
      metadata_url: data.metadata_url ?? '',
      metadata_file: data.metadata_file ?? '',
      attr_email: data.attr_email ?? '',
      attr_first_name: data.attr_first_name ?? '',
      attr_last_name: data.attr_last_name ?? '',
      attr_roles: data.attr_roles ?? '',
    }),
  });

  useEffect(() => {
    // Only 403 should land on /unauthorized. 401 is handled globally by
    // ApiClient, which navigates to /login after clearing client state.
    if (isForbiddenError(errorMetadata) || isForbiddenError(errorProvider)) {
      navigate({ to: '/unauthorized' });
    }
  }, [errorMetadata, errorProvider, navigate]);

  // Memoize providerDefaults for form default values
  const providerDefaults = useMemo(
    () => ({
      provider_name: provider?.provider_name ?? '',
      email_domain: provider?.email_domain ?? '',
      metadata_url: provider?.metadata_url ?? '',
      metadata_file: provider?.metadata_file ?? '',
      attr_email: provider?.attr_email ?? '',
      attr_first_name: provider?.attr_first_name ?? '',
      attr_last_name: provider?.attr_last_name ?? '',
      attr_roles: provider?.attr_roles ?? '',
    }),
    [provider],
  );

  // Zod schema for validation
  const samlSchema = useMemo(
    () =>
      z
        .object({
          provider_name: z.string().min(1, t('saml.provider_name_required')),
          email_domain: z.string().min(1, t('saml.email_domain_required')),
          attr_email: z.string().min(1, t('saml.attr_email_required')),
          attr_first_name: z.string().min(1, t('saml.attr_first_name_required')),
          attr_last_name: z.string().min(1, t('saml.attr_last_name_required')),
          attr_roles: z.string().min(1, t('saml.attr_roles_required')),
          metadata_url: z.string().optional(),
          metadata_file: z.string().optional(),
        })
        .refine(data => data.metadata_url || data.metadata_file, {
          message: t('saml.metadata_required'),
          path: ['metadata_url', 'metadata_file'],
        }),
    [t],
  );

  const form = useForm({
    defaultValues: providerDefaults,
    validators: {
      onChange: ({ value }) => {
        const result = samlSchema.safeParse(value);
        if (!result.success) {
          return result.error.message;
        }
        return undefined;
      },
      onSubmit: async ({ value, formApi }) => {
        if (!value.metadata_url && !value.metadata_file) {
          toast.error(t('saml.metadata_required'));
          return;
        }
        const result = samlSchema.safeParse(value);

        if (!result.success) {
          // Gather all field errors from formApi
          const allErrors = [
            ...new Set([
              ...Object.entries(formApi.state.fieldMeta)
                .map(([field, meta]) => (meta?.errors?.[0] ? `${field}: ${meta.errors[0]}` : null))
                .filter((x): x is string => x !== null),
              ...(result.error
                ? result.error.issues.map((issue: z.core.$ZodIssue) => `${issue.path.join('.')}: ${issue.message}`)
                : []),
            ]),
          ];

          // Show all errors in a toast
          toast.error(
            <div>
              <b>{t('saml.please_fix_errors')}</b>
              <ul>
                {allErrors.map(err => (
                  <li key={String(err)}>{err}</li>
                ))}
              </ul>
            </div>,
          );
          return;
        }

        const formData = new FormData();
        formData.append('provider_name', value.provider_name);
        formData.append('email_domain', value.email_domain);

        formData.append('attr_email', value.attr_email);
        formData.append('attr_first_name', value.attr_first_name);
        formData.append('attr_last_name', value.attr_last_name);
        formData.append('attr_roles', value.attr_roles);

        if (value.metadata_url) {
          formData.append('metadata_url', value.metadata_url);
        }
        if (fileRef.current instanceof File) {
          formData.append('metadata_file', fileRef.current);
        }

        // Show loading toast and keep its ID
        const toastId = toast.loading(t('saml.updating_config'));

        try {
          // Use canonical apiClient to perform the POST. apiClient handles credentials and
          // will skip default headers for FormData uploads.
          await apiClient.post('/auth/saml/provider', formData, { skipDefaultHeaders: true });
          toast.success(t('saml.config_success'), { id: toastId });
          refetchProvider();
          return undefined;
        } catch (error: unknown) {
          const message = (error as Error)?.message ?? t('saml.config_error');
          toast.error(message, { id: toastId });
          throw error;
        }
      },
    },
  });

  const formRef = useRef(form);

  // Update formRef when form changes
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  // Reset form when provider changes
  useEffect(() => {
    formRef.current.reset(providerDefaults);
  }, [providerDefaults]);

  // Memoized file select handler
  const handleFileSelect = useCallback(
    (fileData: unknown) => {
      if (Array.isArray(fileData)) {
        fileRef.current = fileData[0] ?? null;
      } else if (fileData instanceof File) {
        fileRef.current = fileData;
      } else {
        fileRef.current = null;
      }
      form.setFieldValue('metadata_file', fileRef.current?.name ?? '');
    },
    [form],
  );

  const values = form.state.values || {};

  // Disable submit if loading or error
  const disableSubmit = loadingMetadata || loadingProvider || errorMetadata || errorProvider;

  return (
    <form
      className={styles.form}
      onSubmit={async e => {
        e.preventDefault();
        e.stopPropagation();

        await form.handleSubmit();
      }}
    >
      <Stack fullHeight fullWidth gap={6} orientation="vertical">
        <Tabs className={styles.tabs} data-testid={'auth-tabs'}>
          <TabList aria-label={t('saml.aria_tablist_auth_settings')} className={styles.tabList}>
            <Tab data-testid="tab-saml" id="saml">
              <Stack alignItems="center" as="span" gap={1} orientation="horizontal">
                <Lock size={14} />
                {t('saml.saml_tab')}
              </Stack>
            </Tab>
          </TabList>

          <TabPanel
            className={clsx(
              styles.tabContents,
              getStackClassName('displayflex'),
              getStackClassName('gap4'),
              getStackClassName('vertical'),
              scrollbars,
            )}
            data-testid="tabpanel-saml"
            id="saml"
          >
            <Popover
              isOpen={showOptions}
              data-testid="auth-popover"
              content={
                <Group orientation="vertical">
                  <Button
                    data-testid={'refresh-metadata-btn'}
                    onPress={isFetchingMetadata ? noop : () => refetchMetadata()}
                    size="sm"
                    visual={isFetchingMetadata ? 'primary' : 'default'}
                  >
                    {isFetchingMetadata ? t('saml.refreshing_metadata') : t('saml.refresh_metadata')}
                  </Button>
                  <Button
                    data-testid={'refresh-provider-btn'}
                    onPress={isFetchingProvider ? noop : () => refetchProvider()}
                    size="sm"
                    visual={isFetchingProvider ? 'primary' : 'default'}
                  >
                    {isFetchingProvider ? t('saml.refreshing_provider') : t('saml.refresh_provider')}
                  </Button>
                </Group>
              }
              onClickOutside={onCloseHandler}
              positions={['bottom']}
            >
              <Button
                className={styles.options}
                data-testid="saml-options-btn"
                onPress={() => setShowOptions(s => !s)}
                visual="unstyled"
              >
                <Tooltip
                  content={t('saml.options_tooltip')}
                  hoverOptions={{ delay: 1000 }}
                  isDisabled={showOptions}
                  placement="left-start"
                  trigger={<Cog color="white" size={24} />}
                />
              </Button>
            </Popover>
            <FormSectionTitle
              label={t('saml.portal_parameters')}
              tooltip={t('saml.portal_parameters_tooltip')}
              aria-label={t('saml.aria_portal_parameters')}
            />
            <Stack fullWidth gap={4} orientation="vertical">
              <table width="100%">
                <tbody>
                  <tr>
                    <td align="left">{t('saml.recipient')}</td>
                    <td align="left">
                      <CopyText id={'saml-recipient'} value={samlMetadata?.recipient} />
                    </td>
                  </tr>
                  <tr>
                    <td align="left">{t('saml.audience')}</td>
                    <td align="left">
                      <CopyText id={'saml-audience'} value={samlMetadata?.audience} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Stack>

            <FormSectionTitle
              label={t('saml.identity_provider')}
              tooltip={t('saml.identity_provider_tooltip')}
              aria-label={t('saml.aria_identity_provider')}
            />

            <Stack className={styles.formSection} fullWidth gap={6} orientation="vertical">
              <form.Field
                name="provider_name"
                validators={{
                  onChange: value => (!value ? t('saml.provider_name_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-provider_name"
                      label={{
                        children: t('saml.provider_name_label'),
                        htmlFor: 'provider_name',
                        id: 'label-provider_name',
                      }}
                      id="provider_name"
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'provider_name-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="provider_name-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>
              <form.Field
                name="email_domain"
                validators={{
                  onChange: value => (!value ? t('saml.email_domain_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-email_domain"
                      id="email_domain"
                      label={{
                        children: t('saml.email_domain_label'),
                        htmlFor: 'email_domain',
                        id: 'label-email_domain',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'email_domain-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="email_domain-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>

              <Stack alignItems="center" fullWidth gap={4} justifyContent="space-between" orientation="horizontal">
                <form.Field name="metadata_url">
                  {field => (
                    <>
                      <Input
                        {...sharedInputProps}
                        data-testid="input-metadata_url"
                        id="metadata_url"
                        label={{
                          children: t('saml.metadata_url_label'),
                          htmlFor: 'metadata_url',
                          id: 'label-metadata_url',
                        }}
                        onChange={e => field.handleChange(e.target.value)}
                        value={field.state.value || ''}
                        aria-describedby={field.state.meta.errors?.[0] ? 'metadata_url-error' : undefined}
                      />
                      {field.state.meta.errors?.[0] && (
                        <Text as="span" id="metadata_url-error">
                          {field.state.meta.errors[0]}
                        </Text>
                      )}
                    </>
                  )}
                </form.Field>

                <Text as="span">{t('saml.or')}</Text>

                <Upload
                  acceptedFileTypes={['.xml']}
                  buttonProps={{
                    style: { minWidth: 400 },
                    visual: 'primary',
                  }}
                  buttonText={
                    values.metadata_file || ''
                      ? t('saml.selected_metadata_file', {
                          file: values.metadata_file,
                        })
                      : t('saml.upload_metadata_xml')
                  }
                  data-testid="input-metadata_file"
                  onSelect={handleFileSelect}
                />
                {values.metadata_file && (
                  <Button
                    data-testid={'clear-file-btn'}
                    onPress={() => {
                      fileRef.current = null;
                      form.setFieldValue('metadata_file', '');
                    }}
                    size="xs"
                    visual="unstyled"
                  >
                    {t('saml.clear_file')}
                  </Button>
                )}
              </Stack>
            </Stack>

            <FormSectionTitle
              label={t('saml.user_attribute_mapping')}
              tooltip={t('saml.user_attribute_mapping_tooltip')}
              aria-label={t('saml.aria_user_attribute_mapping')}
            />

            <Stack className={styles.formSection} fullWidth gap={6} orientation="vertical">
              <form.Field
                name="attr_email"
                validators={{
                  onChange: value => (!value ? t('saml.attr_email_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-attr_email"
                      id="attr_email"
                      label={{
                        children: t('saml.attr_email_label'),
                        htmlFor: 'attr_email',
                        id: 'label-attr_email',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'attr_email-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="attr_email-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>
              <form.Field
                name="attr_first_name"
                validators={{
                  onChange: value => (!value ? t('saml.attr_first_name_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-attr_first_name"
                      id="attr_first_name"
                      label={{
                        children: t('saml.attr_first_name_label'),
                        htmlFor: 'attr_first_name',
                        id: 'label-attr_first_name',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'attr_first_name-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="attr_first_name-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>

              <form.Field
                name="attr_last_name"
                validators={{
                  onChange: value => (!value ? t('saml.attr_last_name_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-attr_last_name"
                      id="attr_last_name"
                      label={{
                        children: t('saml.attr_last_name_label'),
                        htmlFor: 'attr_last_name',
                        id: 'label-attr_last_name',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'attr_last_name-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="attr_last_name-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>
              <form.Field
                name="attr_roles"
                validators={{
                  onChange: value => (!value ? t('saml.attr_roles_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-attr_roles"
                      id="attr_roles"
                      label={{
                        children: t('saml.attr_roles_label'),
                        htmlFor: 'attr_roles',
                        id: 'label-attr_roles',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'attr_roles-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="attr_roles-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>
            </Stack>

            <form.Subscribe
              selector={state => [
                state.canSubmit,
                state.isSubmitting,
                state.isDirty,
                state.isValid,
                state.values,
                state.errors,
              ]}
            >
              {([canSubmit, isSubmitting, isDirty, isValid]) => (
                <Stack fullWidth>
                  <Button
                    data-testid={'configure-submit-btn'}
                    isDisabled={!!disableSubmit || !canSubmit || !isDirty || !isValid}
                    size="lg"
                    type="submit"
                    visual={isSubmitting ? 'success' : 'primary'}
                  >
                    {isSubmitting ? t('saml.configuring') : t('saml.configure_provider')}
                  </Button>
                </Stack>
              )}
            </form.Subscribe>
          </TabPanel>
        </Tabs>
      </Stack>
    </form>
  );
};

export default AuthPage;
